'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';

const navLinks = [
  { label: 'Services', href: '#services' },
  { label: 'Work', href: '#work' },
  { label: 'Contact', href: '#contact' },
  { label: 'About', href: '#about' },
];

function scrollToSection(id: string) {
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: 'smooth' });
  }
}

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleNavClick = (href: string) => {
    const sectionId = href.replace('#', '');
    if (pathname === '/home' || pathname === '/') {
      scrollToSection(sectionId);
    } else {
      router.push(`/home?section=${sectionId}`);
    }
    setMenuOpen(false);
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const section = params.get('section');
      if (section) {
        setTimeout(() => scrollToSection(section), 300);
      }
    }
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-black/95 backdrop-blur-md' : 'bg-transparent'
      }`}
      style={{
        padding: scrolled ? '12px 0' : '20px 0',
        borderBottom: '1px solid rgba(255,255,255,0.15)',
      }}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <Link href="/home" className="flex items-center gap-2">
          <span
            className="text-white"
            style={{
              fontFamily: 'Bebas Neue, sans-serif',
              fontSize: '22px',
              letterSpacing: '4px',
              lineHeight: 1,
            }}
          >
            ALLYWRK
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-10">
          {navLinks?.map((item) => (
            <button
              key={item?.label}
              onClick={() => handleNavClick(item?.href)}
              className="text-white/70 hover:text-white transition-colors duration-300"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '12px',
                letterSpacing: '2.5px',
                fontWeight: 400,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                paddingBottom: '4px',
              }}
            >
              {item?.label}
            </button>
          ))}
        </nav>

        {/* CTA */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => handleNavClick('#contact')}
            className="hidden md:inline-flex items-center btn-lime"
            style={{ fontSize: '12px', letterSpacing: '2.5px', padding: '10px 24px' }}
          >
            Book A Session
          </button>
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="md:hidden w-9 h-9 flex flex-col justify-center gap-1.5 items-center"
            aria-label="Toggle menu"
          >
            <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? 'rotate-45 translate-y-2' : ''}`} />
            <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? 'opacity-0' : ''}`} />
            <span className={`block w-6 h-0.5 bg-white transition-all duration-300 ${menuOpen ? '-rotate-45 -translate-y-2' : ''}`} />
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div className={`md:hidden transition-all duration-400 overflow-hidden ${menuOpen ? 'max-h-80 opacity-100' : 'max-h-0 opacity-0'}`}>
        <div className="bg-black/95 backdrop-blur-md px-6 py-6 flex flex-col gap-5 border-t border-white/10">
          {navLinks?.map((item) => (
            <button
              key={item?.label}
              onClick={() => handleNavClick(item?.href)}
              className="text-left text-white/70 hover:text-white transition-colors"
              style={{
                fontFamily: 'DM Sans, sans-serif',
                fontSize: '12px',
                letterSpacing: '2.5px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              {item?.label}
            </button>
          ))}
          <button
            onClick={() => handleNavClick('#contact')}
            className="mt-2 btn-lime text-center"
            style={{ fontSize: '12px', letterSpacing: '2.5px' }}
          >
            Book A Session
          </button>
        </div>
      </div>
    </header>
  );
}